// Materialises RFC-101 (engine-adapter production contracts) — Phase 2.
// See: geosoft-platform-strategy/docs/appendix/rfcs/RFC-101-engine-adapter-production-contracts.md
//
// This module enriches the @geosoft/viewer-sdk EngineAdapter surface with the
// production-grade contract pieces RFC-101 P1..P8 demand:
//   P1 EngineCapabilities (capability declaration at registration)
//   P2 LayerHandle (opaque, returned by addLayer)
//   P3 LAYER_KIND_MATRIX (production-required layer kinds per engine)
//   P4 HostBridge (interleaving contract)
//   P5 (no new types — semantics)
//   P6 AdapterEvent (error reporting; no in-adapter UI)
//   P7 (no new types — defenceVariantSafe boolean on capabilities is enforced
//      by host at registration)
//   P8 (no new types — addPlugin is on EngineAdapter optional)
//
// Existing adapters keep working: every new field on EngineAdapter is OPTIONAL
// in the SDK stub. Each adapter is updated in the same PR to expose `capabilities`.

import type { EngineId, LayerKind } from '@geosoft/viewer-sdk';

// ============================================================================
// P1. EngineCapabilities
// ============================================================================

export type ThreeDTilesSupport = 'none' | '1.0' | '1.1';
export type PluginKind =
  | 'measurement'
  | 'annotation'
  | 'model-tree'
  | 'export'
  | 'profile'
  | 'viewshed';

/**
 * Static, declarative capability declaration. Host inspects this at
 * registration; layers route to the right adapter based on it (instead of
 * hard-coded engine selection in AppShell or the host).
 */
export interface EngineCapabilities {
  readonly engineId: EngineId;
  readonly supportedLayerKinds: ReadonlyArray<LayerKind>;
  readonly supportsTime: boolean;
  readonly supportsTerrain: boolean;
  readonly supports3DTiles: ThreeDTilesSupport;
  /**
   * Engines this adapter can be interleaved with as the secondary engine.
   * Empty array = does not participate in interleaving.
   */
  readonly supportsInterleaving: ReadonlyArray<EngineId>;
  /**
   * If false, the host filters this adapter out of the registered set when
   * the defence-variant flag is on at startup (RFC-101 P7).
   */
  readonly defenceVariantSafe: boolean;
  /**
   * Plugin kinds this adapter can host directly (RFC-101 P8).
   * Empty array = the adapter does not provide a plugin host surface;
   * plugins target the ViewerHost itself.
   */
  readonly pluginHostKinds: ReadonlyArray<PluginKind>;
}

// ============================================================================
// P2. LayerHandle (opaque) + ViewState
// ============================================================================

/**
 * Opaque handle returned by addLayer. Engines may carry their own state inside
 * but consumers MUST treat the handle as opaque.
 *
 * Intentionally a branded string: at runtime the value is the layer.id, but the
 * branding prevents accidental string-passing.
 */
export type LayerHandle = string & { readonly __layerHandle: unique symbol };

export function asLayerHandle(layerId: string): LayerHandle {
  return layerId as LayerHandle;
}

export interface ViewState {
  readonly center: [number, number];
  readonly zoom?: number;
  readonly heading?: number;
  readonly pitch?: number;
  /**
   * Engine-specific extension behind a typed key. Adapters that need to carry
   * extra state (e.g. Cesium height/roll, Three.js camera matrix) put it here
   * keyed by their engineId.
   */
  readonly engineExt?: Partial<Record<EngineId, unknown>>;
}

// ============================================================================
// P4. HostBridge (interleaving contract)
// ============================================================================

export type InterleavingEvent =
  | { type: 'view-changed'; payload: ViewState }
  | { type: 'time-changed'; payload: Date | null };

/**
 * The host hands a HostBridge to a secondary adapter when interleaving with a
 * primary engine (e.g. deck.gl-on-MapLibre). The host is responsible for only
 * issuing this when the secondary's capabilities.supportsInterleaving includes
 * the primary engineId.
 */
export interface HostBridge {
  readonly primaryEngine: EngineId;
  /**
   * Engine-specific handle on the primary (Map / Viewer / Scene / Renderer).
   * Adapters cast this to the type their interleaving target needs.
   */
  readonly primaryHandle: unknown;
  /** 0 = below primary; positive = above. */
  readonly zIndex: number;
  /** When true, the secondary follows the primary's view state. */
  readonly syncedView: boolean;
  notify(event: InterleavingEvent): void;
}

// ============================================================================
// P6. AdapterEvent (error reporting; no in-adapter UI dialogs)
// ============================================================================

export interface AdapterErrorPayload {
  readonly code: string;
  readonly message: string;
  readonly layerId?: string;
  /** True = host should retry / surface a non-blocking toast; False = surface as a hard error. */
  readonly recoverable: boolean;
}

export type AdapterEvent =
  | { type: 'error'; payload: AdapterErrorPayload }
  | { type: 'view-changed'; payload: ViewState }
  | { type: 'time-changed'; payload: Date | null }
  | { type: 'layer-added'; payload: { handle: LayerHandle } }
  | { type: 'layer-removed'; payload: { handle: LayerHandle } };

/**
 * Thrown by adapter ops that the engine cannot satisfy.
 * The host catches and either falls back gracefully or routes to another adapter.
 *
 * Carries engineId + feature for telemetry + diagnostic clarity per RFC-101 P2.
 */
export class UnsupportedFeatureError extends Error {
  readonly engineId: EngineId;
  readonly feature: string;
  constructor(engineId: EngineId, feature: string, hint?: string) {
    super(
      `Adapter '${engineId}' does not support feature '${feature}'${
        hint ? `: ${hint}` : ''
      }`,
    );
    this.name = 'UnsupportedFeatureError';
    this.engineId = engineId;
    this.feature = feature;
  }
}

// ============================================================================
// P3. Layer-kind production matrix
// ============================================================================

/**
 * The production-required (✅ MUST) layer-kind matrix from RFC-101 P3.
 * Phase 2 closes only when each adapter supports its MUST set.
 *
 * Adapters MAY support more (the SHOULD column in the RFC) but MUST support
 * the kinds listed here. The host runtime asserts adapter.capabilities.supportedLayerKinds
 * is a superset of MATRIX[adapter.id] at registration via assertMatrixCompliance().
 */
export const LAYER_KIND_MATRIX: Readonly<Record<EngineId, ReadonlyArray<LayerKind>>> = {
  maplibre: ['raster', 'vector'],
  openlayers: ['raster', 'terrain'],
  deckgl: ['raster', 'vector', '3dtiles'],
  cesium: ['raster', '3dtiles', 'terrain', 'pointcloud'],
  three: ['bim'],
  giro3d: ['raster', 'terrain', '3dtiles', 'bim'],
  potree: ['pointcloud'],
};

/**
 * The optional (⚪ SHOULD) layer-kind set per engine. For documentation +
 * validator visibility; not enforced.
 */
export const LAYER_KIND_MATRIX_SHOULD: Readonly<
  Record<EngineId, ReadonlyArray<LayerKind>>
> = {
  maplibre: ['panorama', 'sensor'],
  openlayers: [],
  deckgl: ['pointcloud', 'panorama', 'sensor', 'timeseries'],
  cesium: ['vector', 'bim', 'panorama', 'sensor', 'timeseries'],
  three: ['3dtiles', 'pointcloud', 'panorama'],
  giro3d: ['pointcloud', 'panorama'],
  potree: [],
};
